import {
  deriveNavigation,
  pageAvailability,
  validateProducts,
  type LandedCapabilities,
  type ProductsDeclaration,
} from '@hurdl/admin-panel-backend/product';

/**
 * The Products this deployment of the Admin_Panel surveys (R1.2).
 *
 * One Product — the fast-food menus product — declaring no Declared_Pages and one
 * Bespoke_Page: the allergen-booklet page. A new Product is a new entry here, not a
 * code change; a Bespoke_Page is the one code exception, registered in
 * `bespoke/registry` under the key this names.
 *
 * **This Product enables no Declared_Page.** The Support, Billing, Web_Analytics and
 * Entitlements capability pages are the full Admin_Panel programme's; this Product has
 * no support/billing/users capability of its own, and the panel's own administration —
 * Users and Settings — is panel-admin, a distinct section of the shell (the `/admin/*`
 * routes), not a per-Product Declared_Page. So `enabledPages` is empty.
 *
 * The allergen-booklet Bespoke_Page stays declared so its `$pageKey` route still
 * resolves — a reviewer still reaches the source PDF — but it is reached from within
 * the menus and review views (the "View PDF" dialog), not from the sidebar; the shell
 * lists no allergen-booklet nav item.
 *
 * The review-and-edit work the earlier menu-workspace Bespoke_Page held is first-class
 * routes now (`/products/$productKey/review` and `.../menus`, with a Restaurant's menu,
 * item editor and categories nested beneath `.../menus/$restaurantKey`), so it is not a
 * Bespoke_Page and does not appear here. The Menus_Backend's review router is
 * still mounted by the backend at `/products/uk-fast-food-menus/bespoke/menu-workspace`
 * — the relative base those routes' operations resolve against — but that is the API
 * path, not a declared UI page.
 *
 * Validated here at module load, as the backend validates it at composition, so a
 * malformed declaration fails fast rather than rendering a broken navigation.
 */
export const PRODUCTS: ProductsDeclaration = validateProducts([
  {
    key: 'uk-fast-food-menus',
    label: 'UK Fast Food Menus',
    mail: { domains: [], organisations: [] },
    subjectKinds: [],
    standingFields: [],
    enabledPages: [],
    bespokePages: [
      {
        key: 'allergen-booklet',
        path: 'allergen-booklet',
        label: 'Allergen booklet',
      },
    ],
  },
]);

/**
 * Which Product-facing capabilities this deployment composed.
 *
 * None, in this build: the Billing, Support, Web_Analytics and Entitlements
 * capabilities are not built, so every Declared_Page is `capability-absent`. The
 * menu-workspace Bespoke_Page needs none of them — it composes the Menus_Backend, which
 * is built — so it is available regardless.
 */
export const LANDED: LandedCapabilities = {
  billing: false,
  support: false,
  analytics: false,
  entitlements: false,
};

/** The navigation derived from the declaration. */
export const NAVIGATION = deriveNavigation(PRODUCTS);

/** The availability of one Declared_Page of one Product in this deployment. */
export { pageAvailability };
